import {
  canonicalizeTagValue,
  getTypedValueLabel,
  normalizeCategoryType,
} from './tagValueUtils';

export function isStartDateParam(name) {
  return name === '_Start_Date';
}

export function isEndDateParam(name) {
  return name === '_End_Date';
}

export function isDateRangeParam(name) {
  return isStartDateParam(name) || isEndDateParam(name);
}

export function parameterLabel(name) {
  if (isStartDateParam(name)) return 'Reporting period start';
  if (isEndDateParam(name)) return 'Reporting period end';
  return String(name || '').replace(/_/g, ' ');
}

// start_date and end_date lead every parameter list: a run is almost always
// bounded in time first and filtered second.
export function compareParameters(a, b) {
  const weight = (param) => (isStartDateParam(param?.name) ? 0 : (isEndDateParam(param?.name) ? 1 : 2));
  const weightA = weight(a);
  const weightB = weight(b);
  if (weightA !== weightB) return weightA - weightB;
  return String(a?.name || '').localeCompare(String(b?.name || ''));
}

export function isRangeParamValue(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && ('gte' in value || 'lte' in value);
}

export const INVALID_DATE_RANGE_MESSAGE = 'Invalid date parameters - Start date must be on or before the end date';

export const DYNAMIC_DATE_TOKENS = [
  { value: 'dynamic:today', label: 'Today' },
  { value: 'dynamic:start_of_month', label: 'Start of Month' },
  { value: 'dynamic:start_of_last_month', label: 'Start of Last Month' },
  { value: 'dynamic:end_of_last_month', label: 'End of Last Month' },
  { value: 'dynamic:last_30_days', label: 'Last 30 Days' },
  { value: 'dynamic:last_90_days', label: 'Last 90 Days' },
  { value: 'dynamic:start_of_year', label: 'Start of Year' },
];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// True when both bounds are complete YYYY-MM-DD dates and the start is after
// the end. Anything else — a blank bound, a half-typed one, a dynamic token an
// automation resolves at firing time — is not this mistake (M41). A single
// date stored as a one-element list counts by its first element.
export function isInvertedDateRange(start, end) {
  const first = (value) => (Array.isArray(value) ? value[0] : value);
  const from = String(first(start) || '').trim();
  const to = String(first(end) || '').trim();
  return ISO_DATE.test(from) && ISO_DATE.test(to) && from > to;
}

// The message for an inverted pair: the reporting period is unnamed, a range
// filter on an ordinary Date column names its column.
export function invalidDateRangeMessage(column = null) {
  return column
    ? `${INVALID_DATE_RANGE_MESSAGE} (${String(column).replace(/_/g, ' ')})`
    : INVALID_DATE_RANGE_MESSAGE;
}

// The first inverted pair in one block's values — `{ column: value }` as a rail
// holds them, where the reporting period is the Start_Date/End_Date pair and a
// range filter holds { gte, lte }. Returns the message to show, or null.
export function findInvertedDateRange(values) {
  const entries = Object.entries(values || {});
  const start = entries.find(([column]) => isStartDateParam(column))?.[1];
  const end = entries.find(([column]) => isEndDateParam(column))?.[1];
  if (isInvertedDateRange(start, end)) return invalidDateRangeMessage();
  const range = entries.find(([, value]) => isRangeParamValue(value) && isInvertedDateRange(value.gte, value.lte));
  return range ? invalidDateRangeMessage(range[0]) : null;
}

export function cleanRangeValue(value) {
  const out = {};
  if (value?.gte && String(value.gte).trim() !== '') out.gte = value.gte;
  if (value?.lte && String(value.lte).trim() !== '') out.lte = value.lte;
  return out;
}

// The "nothing selected" value for a parameter, by the shape its control edits:
// a range filter holds {gte, lte}, a Start_Date/End_Date parameter a single
// date string, and every other filter an array of chosen values. Used by the
// Clear buttons on the Function and View parameter rails.
export function emptyParameterValue(filter) {
  if (filter?.range) return { gte: '', lte: '' };
  if (isDateRangeParam(filter?.column)) return '';
  return [];
}

export function isEmptyParameterValue(value) {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.filter((item) => item != null).length === 0;
  if (typeof value === 'object') return Object.keys(cleanRangeValue(value)).length === 0;
  return String(value).trim() === '';
}

export function resolveParameterType(name, type) {
  if (isDateRangeParam(name)) {
    return 'Date';
  }
  return normalizeCategoryType(type);
}

export function normalizeLoadedParameterValue(value, type) {
  const declaredType = normalizeCategoryType(type);

  if (value === null || value === undefined || value === '') {
    return declaredType === 'Yes/No' ? '' : '';
  }

  if (declaredType === 'Yes/No') {
    if (typeof value === 'boolean') return value;
    const normalized = String(value).trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;
    return '';
  }

  if (declaredType === 'Number') {
    return typeof value === 'number' ? value : String(value);
  }

  if (declaredType === 'Date') {
    return String(value).trim();
  }

  return String(value);
}

export function formatParameterInputValue(value, type, { multi = true, dateRange = false } = {}) {
  const declaredType = normalizeCategoryType(type);

  if (dateRange) {
    return normalizeLoadedParameterValue(Array.isArray(value) ? value[0] : value, declaredType);
  }

  if (Array.isArray(value)) {
    if (multi) {
      return value
        .map((item) => normalizeLoadedParameterValue(item, declaredType))
        .filter((item) => item !== '' && item !== null && item !== undefined)
        .join(', ');
    }
    return normalizeLoadedParameterValue(value[0], declaredType);
  }

  return normalizeLoadedParameterValue(value, declaredType);
}

export function parseParameterInputValue(rawValue, type, {
  multi = true, dateRange = false, allowDynamicDates = false,
} = {}) {
  const declaredType = normalizeCategoryType(type);
  const parseValue = (value) => {
    if (value !== null && typeof value === 'object') {
      throw new Error('Enter a single value');
    }
    if (allowDynamicDates && declaredType === 'Date'
      && DYNAMIC_DATE_TOKENS.some((token) => token.value === value)) {
      return value;
    }
    return canonicalizeTagValue(value, declaredType);
  };

  if (dateRange || !multi) {
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    if (values.length > 1) throw new Error('Choose one value');
    return parseValue(values[0]) ?? '';
  }

  if (Array.isArray(rawValue)) {
    return rawValue
      .filter((value) => value != null && !(typeof value === 'string' && value.trim() === ''))
      .map(parseValue);
  }
  if (rawValue !== null && typeof rawValue === 'object') {
    throw new Error('Enter values separated by commas');
  }
  const parts = String(rawValue ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value !== '');

  return parts.map(parseValue);
}

// Forms keep raw text while it is edited. Only this conversion produces run
// parameters: typed lists, date bounds, and no entry for an empty constraint.
// Invalid drafts return errors and NO parameter map, so a render or caller
// cannot accidentally turn a failed conversion into an unfiltered request.
export function prepareRunParameters(draft, dataSourceBlocks, { allowDynamicDates = false } = {}) {
  const parameters = {};
  const errors = [];
  const plainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  if (draft != null && !plainObject(draft)) {
    return { parameters: null, errors: [{ blockId: '', column: '', message: 'Parameters must be an object' }] };
  }
  Object.entries(draft || {}).forEach(([blockId, supplied]) => {
    const block = dataSourceBlocks?.[blockId];
    if (!block || (supplied != null && !plainObject(supplied))) {
      errors.push({ blockId, column: '', message: block ? 'Source parameters must be an object' : 'This source no longer exists' });
      return;
    }
    const blockParameters = {};
    const seen = new Set();
    const readFilter = (column, rawValue, mode) => {
      try {
        const filter = (block.filters || []).find((candidate) => candidate.column === column);
        if (!filter?.variable) throw new Error('This filter is no longer variable');
        if (seen.has(column)) throw new Error('Choose either IS or NOT for this filter');
        seen.add(column);
        const type = resolveParameterType(column, filter.type);
        if (type === 'Date' && !isDateRangeParam(column) && !filter.range) {
          throw new Error('This Date filter requires a range; migrate its saved configuration');
        }
        if (Boolean(filter.range) !== (mode === 'range_filters')) {
          throw new Error(filter.range ? 'This filter requires a date range' : 'This filter requires selected values');
        }
        if (mode === 'range_filters') {
          if (type !== 'Date') throw new Error('Ranges require a Date column');
          if (rawValue == null || rawValue === '' || (Array.isArray(rawValue) && rawValue.length === 0)) {
            rawValue = {};
          }
          if (!plainObject(rawValue) || Object.keys(rawValue).some((key) => !['gte', 'lte'].includes(key))) {
            throw new Error('Enter a range with From and/or To dates');
          }
          const range = {};
          for (const bound of ['gte', 'lte']) {
            const value = parseParameterInputValue(rawValue[bound], 'Date', { multi: false, allowDynamicDates });
            if (value !== '') range[bound] = value;
          }
          if (isInvertedDateRange(range.gte, range.lte)) throw new Error(invalidDateRangeMessage(column));
          if (Object.keys(range).length > 0) {
            blockParameters.range_filters = { ...blockParameters.range_filters, [column]: range };
          }
        } else {
          const singleDate = isDateRangeParam(column);
          if (singleDate && mode === 'exclude_filters') throw new Error('A reporting date cannot use NOT');
          const value = parseParameterInputValue(rawValue, type, {
            multi: !singleDate, dateRange: singleDate, allowDynamicDates,
          });
          if (singleDate ? value !== '' : value.length > 0) {
            if (mode === 'exclude_filters') {
              blockParameters.exclude_filters = { ...blockParameters.exclude_filters, [column]: value };
            } else {
              blockParameters[column] = value;
            }
          }
        }
      } catch (error) {
        errors.push({ blockId, column, message: error.message });
      }
    };
    Object.entries(supplied || {}).forEach(([key, value]) => {
      if (key === 'exclude_filters' || key === 'range_filters') {
        if (!plainObject(value)) {
          errors.push({ blockId, column: key, message: 'Filters must be an object keyed by column' });
          return;
        }
        Object.entries(value).forEach(([column, rawValue]) => readFilter(column, rawValue, key));
      } else {
        readFilter(key, value, 'include');
      }
    });
    const inverted = findInvertedDateRange(blockParameters);
    if (inverted) errors.push({ blockId, column: '_End_Date', message: inverted });
    if (Object.keys(blockParameters).length > 0) parameters[blockId] = blockParameters;
  });
  return { parameters: errors.length > 0 ? null : parameters, errors };
}

// Adapt the Function/View controls to the same draft shape automations edit.
export function prepareParameterControls(formData, dataSourceBlocks, excluded = {}, prefix = '') {
  const draft = {};
  Object.entries(formData || {}).forEach(([blockId, block]) => {
    const values = {};
    Object.entries(block?.data || {}).forEach(([column, value]) => {
      const filter = (dataSourceBlocks?.[blockId]?.filters || []).find((candidate) => candidate.column === column);
      if (filter && !filter.variable) return;
      if (filter?.range) {
        values.range_filters = { ...values.range_filters, [column]: value };
      } else if (excluded[`${prefix}${blockId}::${column}`]) {
        values.exclude_filters = { ...values.exclude_filters, [column]: value };
      } else {
        values[column] = value;
      }
    });
    draft[blockId] = values;
  });
  return prepareRunParameters(draft, dataSourceBlocks);
}

export function formatParameterErrors(errors, dataSourceBlocks) {
  return (errors || []).map(({ blockId, column, message }) => {
    const source = dataSourceBlocks?.[blockId]?.name || blockId;
    const label = [source, column ? parameterLabel(column) : null].filter(Boolean).join(' / ');
    return label ? `${label}: ${message}` : message;
  }).join('\n');
}

// A historical run may use today's implementation only when its input contract
// still agrees. Defaults are deliberately excluded: the recorded values win.
//
// Each difference carries the `message` shown beside a field, and a `kind`,
// the source's name and (for a type change) both types, which
// `describeContractChanges` turns into the sentences a View card lists.
export function parameterCompatibilityErrors(previousBlocks, currentBlocks) {
  const differences = [];
  const blockIds = new Set([...Object.keys(previousBlocks || {}), ...Object.keys(currentBlocks || {})]);
  for (const blockId of blockIds) {
    const previous = previousBlocks?.[blockId];
    const current = currentBlocks?.[blockId];
    const sourceName = (current || previous).name || blockId;
    const sourceDifference = (kind, message) => ({ blockId, column: '', sourceName, kind, message });
    if (!previous) {
      differences.push(sourceDifference('source_added', 'This source was added'));
      continue;
    }
    if (!current) {
      differences.push(sourceDifference('source_removed', 'This source was removed'));
      continue;
    }
    if (previous.table_id !== current.table_id) {
      differences.push(sourceDifference('source_table_changed', 'This source now reads a different table'));
      continue;
    }
    const before = new Map((previous.filters || []).map((filter) => [filter.column, filter]));
    const after = new Map((current.filters || []).map((filter) => [filter.column, filter]));
    for (const column of new Set([...before.keys(), ...after.keys()])) {
      const oldFilter = before.get(column);
      const newFilter = after.get(column);
      if (!oldFilter?.variable && !newFilter?.variable) continue;
      const filterDifference = (kind, message, types = {}) => ({
        blockId, column, sourceName, kind, message, ...types,
      });
      if (!oldFilter) {
        differences.push(filterDifference('parameter_added', 'This parameter was added'));
        continue;
      }
      if (!newFilter) {
        differences.push(filterDifference('parameter_removed', 'This parameter was removed'));
        continue;
      }
      if (Boolean(oldFilter.variable) !== Boolean(newFilter.variable)) {
        differences.push(newFilter.variable
          ? filterDifference('parameter_now_variable', 'This filter is now variable')
          : filterDifference('parameter_now_fixed', 'This filter is now fixed'));
        continue;
      }
      if (Boolean(oldFilter.range) !== Boolean(newFilter.range)) {
        differences.push(newFilter.range
          ? filterDifference('parameter_now_range', 'This filter now requires a date range')
          : filterDifference('parameter_now_values', 'This filter now requires selected values'));
        continue;
      }
      const previousType = resolveParameterType(column, oldFilter.type);
      const currentType = resolveParameterType(column, newFilter.type);
      if (previousType !== currentType) {
        differences.push(filterDifference(
          'parameter_type_changed',
          `This filter changed from ${previousType} to ${currentType}`,
          { previousType, currentType },
        ));
      }
    }
  }
  return differences;
}

// One plain sentence per difference `parameterCompatibilityErrors` found, for
// the list a View card shows when its Function's data sources changed.
export function describeContractChanges(differences) {
  return (differences || []).map((difference) => {
    const { sourceName, column } = difference;
    const parameter = `${sourceName} / ${parameterLabel(column)}`;
    switch (difference.kind) {
      case 'source_added':
        return `${sourceName} was added as a data source.`;
      case 'source_removed':
        return `${sourceName} was removed as a data source.`;
      case 'source_table_changed':
        return `${sourceName} now reads a different table.`;
      case 'parameter_added':
        return `${parameter} was added.`;
      case 'parameter_removed':
        return `${parameter} was dropped: the new version no longer has this parameter.`;
      case 'parameter_now_fixed':
        return `${parameter} was dropped: it is now fixed by the function.`;
      case 'parameter_now_variable':
        return `${parameter} is now a parameter.`;
      case 'parameter_now_range':
        return `${parameter} now takes a date range.`;
      case 'parameter_now_values':
        return `${parameter} now takes selected values.`;
      case 'parameter_type_changed':
        return `${parameter} changed from ${difference.previousType} to ${difference.currentType}.`;
      default:
        throw new Error(`Unknown contract difference: ${difference.kind}`);
    }
  });
}

export function getParameterValueLabel(value, type, options = []) {
  const match = (options || []).find((option) => option?.value === value);
  return getTypedValueLabel(value, type, match?.name ?? null);
}

export function getParameterOptionKey(value) {
  if (typeof value === 'string') return `s:${value}`;
  if (typeof value === 'number') return `n:${value}`;
  if (typeof value === 'boolean') return `b:${value}`;
  return `o:${JSON.stringify(value)}`;
}

export function inferTypeFromValue(value) {
  if (typeof value === 'boolean') return 'Yes/No';
  if (typeof value === 'number') return 'Number';
  // A string is Text even when it looks like a date: types come from
  // declarations, never from value shape (M66).
  return 'Text';
}

export function getStoredValueLabel(value) {
  return getTypedValueLabel(value, inferTypeFromValue(value));
}

// What makes two same-named variable filters "the same control": the column's
// type, whether it is a range or a value, and its default. The Dashboard, a
// View and the Automation editor merge controls across data-source blocks only
// where every block's filter has the same signature.
export const filterSignature = (filter) => JSON.stringify([
  resolveParameterType(filter?.column, filter?.type),
  Boolean(filter?.range),
  filter?.range
    ? { gte: filter.range.gte || '', lte: filter.range.lte || '' }
    : (Array.isArray(filter?.value) && filter.value.length > 0 ? filter.value : null),
]);

// The unified columns one control would misrepresent: every block carrying the
// column has the same variable filter signature (so the Dashboard shows ONE
// control for it), yet the blocks hold different values or different IS/NOT
// bits — a state only per-block editing or a historical re-run can produce.
// The unified control would render blank while a run sent both values (M218).
// `excluded` is the Dashboard's `{ "<blockId>::<column>": true }` map.
export function hiddenDisagreements(formData, excluded, dataSourceBlocks) {
  const byColumn = {};
  Object.entries(formData || {}).forEach(([blockId, blockObj]) => {
    const filters = dataSourceBlocks?.[blockId]?.filters || [];
    Object.entries(blockObj?.data || {}).forEach(([column, value]) => {
      const filter = filters.find((candidate) => candidate.column === column);
      if (!byColumn[column]) {
        byColumn[column] = { signatures: new Set(), values: new Set(), excluded: new Set() };
      }
      const entry = byColumn[column];
      entry.signatures.add(filter?.variable ? filterSignature(filter) : 'fixed');
      entry.values.add(JSON.stringify(value ?? ''));
      entry.excluded.add(Boolean(excluded?.[`${blockId}::${column}`]));
    });
  });
  return Object.entries(byColumn)
    .filter(([, entry]) => (
      entry.signatures.size === 1
      && !entry.signatures.has('fixed')
      && (entry.values.size > 1 || entry.excluded.size > 1)
    ))
    .map(([column]) => column);
}

// The parameters a run was executed with, in the form the request and the run
// document both carry (`{ [block_id]: { [column]: values, exclude_filters,
// range_filters } }`), reduced to what they ask for: key order, the order of a
// multi-select's values, and filters that select nothing (an empty list, an
// empty string, a null, an empty block) do not change the data a run returns.
const canonicalRunParameters = (value) => {
  if (Array.isArray(value)) {
    const items = value.map(canonicalRunParameters).filter((item) => item !== undefined);
    if (items.length === 0) return undefined;
    if (!items.every((item) => typeof item !== 'object')) return items;
    // One value asks for the same data as a one-element list of it.
    if (items.length === 1) return items[0];
    return [...items].sort((a, b) => String(a).localeCompare(String(b)));
  }
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).sort().forEach((key) => {
      const item = canonicalRunParameters(value[key]);
      if (item !== undefined) out[key] = item;
    });
    return Object.keys(out).length === 0 ? undefined : out;
  }
  if (value === null || value === undefined || value === '') return undefined;
  return value;
};

// Whether two runs' parameters ask for the same data. A View card shows only
// runs of its function that were executed with the card's own parameters, so
// two cards of one function with different filters never share a result.
export const sameRunParameters = (a, b) => (
  JSON.stringify(canonicalRunParameters(a) ?? null) === JSON.stringify(canonicalRunParameters(b) ?? null)
);
